import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Star, Minus, Plus, Store, Package, ShoppingBag, Zap } from "lucide-react";
import { inr } from "../../lib/api";
import ProductVariantPicker from "../../components/ProductVariantPicker";
import VariantAxisPicker from "../../components/VariantAxisPicker";
import { FullPageLoader } from "../../components/Loaders";
import { ProductAttributes, ReviewBlock } from "./_partials/ProductInfoBlocks";
import { LseBoughtTogether, LseBrandSku } from "../../components/lse/LseProductExtras";
import { useIsBlinkit } from "../../lib/storefrontTheme";
import { useProductController } from "./controllers/useProductController";
import BkProductView from "./blinkit/BkProductView";

function FoodMark({ type }) {
    const cls = type === "NON_VEG" ? "non-veg" : type === "EGG" ? "egg" : "veg";
    return <span className={`food-mark ${cls}`} aria-label={type || "VEG"} />;
}

/**
 * ProductDetailsPage — dedicated route for a single product with the
 * long description, image gallery, variant selection and both
 * product-specific and shop-wide review aggregates. Add-to-cart posts to
 * the same ``/cart/items`` endpoint used by the restaurant menu, so
 * single-shop cart validation continues to work.
 */
export default function ProductDetailsPage() {
    return useIsBlinkit() ? <BkProductView /> : <ClassicProductDetailsPage />;
}

function ClassicProductDetailsPage() {
    const {
        nav, product, shop, imgIdx, setImgIdx, prodSummary, shopSummary, qty, busy, variantPickerOpen,
        setVariantPickerOpen, error, lowStockThreshold, cartCount, enabledVariants, axes, hasVariantChoice, selection,
        pickedVariant, setAxis, displayPrice, displayMrp, trackStock, isSoldOut, isUnavailable, cantAddMore, images,
        postAdd, buyNowBusy, buyNow, onVariantPicked,
    } = useProductController();

    if (error) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 text-center">
                <Package size={40} className="text-muted-foreground mb-3" />
                <p className="font-semibold" data-testid="pdp-error">{error}</p>
                <Link to="/" className="mt-4 text-sm text-primary underline">Back to home</Link>
            </div>
        );
    }
    if (!product) return <FullPageLoader />;

    const isService = product.kind === "SERVICE";
    // iter128 (matching RestaurantCard.jsx / RestaurantDetailPage.jsx) — only
    // show a real customer-rating average, never the model's fake default
    // (Restaurant.rating defaults to 4.0 with zero reviews). shopSummary
    // comes from the /review-summary aggregate; shop.rating does not.
    const shopStars = shopSummary?.count > 0 ? shopSummary.avg : 0;

    return (
        <div className="pb-32 lg:pb-12 lg:grid lg:grid-cols-2 lg:gap-x-10 lg:items-start lg:px-6 lg:pt-6" data-testid="pdp-root">
            {/* Image gallery — swipeable when >1 image, single-image fallback preserves legacy UI */}
            <ImageCarousel
                images={images}
                imgIdx={imgIdx}
                setImgIdx={setImgIdx}
                onBack={() => nav(-1)}
                fallbackName={product.name}
                cartCount={cartCount}
                onViewCart={() => nav("/cart")}
            />

            {/* Header block */}
            <div className="px-4 pt-5 min-w-0 lg:px-0 lg:pt-0">
                <div className="flex items-center gap-2 flex-wrap">
                    {!isService && product.food_type && <FoodMark type={product.food_type} />}
                    {product.is_bestseller && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 inline-flex items-center gap-1">
                            <Star size={10} className="fill-amber-600 text-amber-600" /> Bestseller
                        </span>
                    )}
                    {isUnavailable ? (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full" data-testid="pdp-unavailable-badge">
                            Currently unavailable
                        </span>
                    ) : isSoldOut && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-red-700 bg-red-100 px-1.5 py-0.5 rounded-full">
                            Sold out
                        </span>
                    )}
                    {!isSoldOut && !isUnavailable && trackStock && product.stock != null && product.stock > 0 && product.stock <= lowStockThreshold && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded-full" data-testid="pdp-low-stock">
                            Only {product.stock} left
                        </span>
                    )}
                </div>
                <h1 className="text-2xl font-display font-bold mt-2" data-testid="pdp-name">{product.name}</h1>
                <LseBrandSku product={product} />

                {/* Shop chip */}
                {shop && (
                    <Link
                        to={`/restaurant/${product.restaurant_id}`}
                        data-testid="pdp-shop-link"
                        className="mt-2 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
                    >
                        <Store size={14} />
                        <span className="font-semibold">{shop.name}</span>
                        {shopStars > 0 && (
                            <span className="inline-flex items-center gap-1 text-xs">
                                <Star size={12} className="fill-amber-500 text-amber-500" />
                                {shopStars.toFixed(1)}
                            </span>
                        )}
                    </Link>
                )}

                {/* Price */}
                <div className="mt-4 flex items-baseline gap-3">
                    {hasVariantChoice && !pickedVariant && (
                        <span className="text-sm text-muted-foreground">from</span>
                    )}
                    <span className="text-2xl font-bold" data-testid="pdp-price">{inr(displayPrice)}</span>
                    {displayMrp && displayMrp > displayPrice && (
                        <>
                            <span className="text-base text-muted-foreground line-through">{inr(displayMrp)}</span>
                            <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                                {Math.round(((displayMrp - displayPrice) / displayMrp) * 100)}% OFF
                            </span>
                        </>
                    )}
                    {product.unit_of_measure && (
                        <span className="text-xs text-muted-foreground">· {product.unit_of_measure}</span>
                    )}
                </div>

                {/* Location Service Engine tenants only (renders nothing otherwise). */}
                <LseBoughtTogether productId={product.id} />

                {/* Variant selector (inline chips) */}
                {hasVariantChoice && (
                    <div className="mt-5" data-testid="pdp-variant-section">
                        <VariantAxisPicker
                            axes={axes}
                            variants={enabledVariants}
                            selection={selection}
                            onChange={setAxis}
                        />
                    </div>
                )}

                {/* iter178 — on phones/tablets this is the fixed bottom bar it always was (position: fixed ignores DOM order);
                    from lg it sits here in the details column, under the price and options, like a desktop product page. */}
                {/* Sticky bottom action bar — Add to Cart (outline) + Buy Now
                    (solid), fixed while scrolling, mirrors the reference
                    marketplace UI. The Add-to-Cart slot swaps to a qty
                    stepper once the item is in the cart, same as before. */}
                {!isService && (
                    <div className="fixed bottom-0 inset-x-0 z-30 bg-card/95 backdrop-blur-md border-t border-border px-4 py-3 safe-pb lg:static lg:inset-auto lg:z-auto lg:mt-6 lg:border-0 lg:bg-transparent lg:backdrop-blur-none lg:p-0">
                        <div className="max-w-lg mx-auto lg:mx-0">
                            {isUnavailable || isSoldOut ? (
                                <div
                                    data-testid={isUnavailable ? "pdp-unavailable" : "pdp-sold-out"}
                                    className="w-full text-center bg-muted text-muted-foreground border-2 border-border font-bold tracking-wider text-xs px-4 py-3 rounded-xl"
                                >
                                    {isUnavailable ? "CURRENTLY UNAVAILABLE" : "SOLD OUT"}
                                </div>
                            ) : (
                                <div className="flex items-stretch gap-3">
                                    {qty === 0 ? (
                                        <button
                                            data-testid="pdp-add-to-cart"
                                            disabled={busy || buyNowBusy}
                                            onClick={() => postAdd(1)}
                                            className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-primary text-primary font-bold tracking-wide text-sm px-4 py-3 rounded-xl active:scale-95 transition disabled:opacity-50"
                                        >
                                            <ShoppingBag size={16} />
                                            ADD TO CART
                                        </button>
                                    ) : (
                                        <div className="flex-1 border-2 border-primary text-primary rounded-xl flex items-center justify-center overflow-hidden">
                                            <button data-testid="pdp-dec" disabled={busy} onClick={() => postAdd(-1)} className="px-3 py-3"><Minus size={16} /></button>
                                            <span className="px-3 font-bold text-sm min-w-[24px] text-center" data-testid="pdp-qty">{qty}</span>
                                            <button data-testid="pdp-inc" disabled={busy || cantAddMore} onClick={() => postAdd(1)} className="px-3 py-3 disabled:opacity-40"><Plus size={16} /></button>
                                        </div>
                                    )}
                                    <button
                                        data-testid="pdp-buy-now"
                                        disabled={busy || buyNowBusy}
                                        onClick={buyNow}
                                        className="flex-1 inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-bold tracking-wide text-sm px-4 py-3 rounded-xl active:scale-95 transition disabled:opacity-50 shadow-md"
                                    >
                                        <Zap size={16} />
                                        BUY NOW
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Description */}
                {product.description && (
                    <div className="mt-6">
                        <h2 className="text-base font-bold mb-2">About this {isService ? "service" : "product"}</h2>
                        <p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-line" data-testid="pdp-description">
                            {product.description}
                        </p>
                    </div>
                )}

                {/* iter127 — Product attributes / details grid. Renders any
                    of the merchant-configured attributes that carry a value
                    for this product (or its currently-picked variant). If
                    ALL rows are empty the whole section is hidden so
                    simple items don't get a bare "Details" heading. */}
                <ProductAttributes product={product} pickedVariant={pickedVariant} shop={shop} lowStockThreshold={lowStockThreshold} />

                {/* Reviews section */}
                <div className="mt-6 border-t border-border pt-5">
                    <h2 className="text-base font-bold mb-3">Customer ratings</h2>
                    <ReviewBlock
                        title="This item"
                        summary={prodSummary}
                        emptyText="No ratings yet for this item."
                    />
                    <div className="h-4" />
                    <ReviewBlock
                        title={shop ? `${shop.name} · overall` : "Shop overall"}
                        summary={shopSummary}
                        emptyText="No ratings yet for this shop."
                    />
                </div>
            </div>

            {variantPickerOpen && (
                <ProductVariantPicker
                    product={product}
                    onConfirm={onVariantPicked}
                    onClose={() => setVariantPickerOpen(false)}
                />
            )}
        </div>
    );
}

/**
 * ImageCarousel — swipeable + auto-playing product image carousel
 * used at the top of the PDP. Falls back to a single static image
 * (no dots, no swipe handlers, no autoplay) when only one image is
 * available, preserving legacy UI for products that pre-date
 * multi-image support.
 *
 * iter123 — autoplay every 4s. Any manual interaction (swipe, dot
 * tap, arrow click) resets the timer AND adds a 3s pause bonus so
 * the user has a beat to read/inspect before the next auto-slide.
 * Timer also pauses whenever the tab is hidden and resumes on
 * refocus — saves CPU on backgrounded PDP tabs.
 *
 * Swipe threshold is 40px so a slow finger drag while reading doesn't
 * accidentally paginate. Left/right arrow buttons render on md+ for
 * mouse users; touch users get the dot indicators + swipe gesture.
 */
const AUTOPLAY_MS = 4000;
const INTERACTION_PAUSE_MS = 3000;

function ImageCarousel({ images, imgIdx, setImgIdx, onBack, fallbackName, cartCount = 0, onViewCart }) {
    const [dragStart, setDragStart] = useState(null);
    const [pauseUntil, setPauseUntil] = useState(0);
    const canPaginate = images.length > 1;

    /** ``bump`` is called on every user-initiated interaction so the
     *  next auto-advance is delayed by AUTOPLAY_MS + INTERACTION_PAUSE_MS
     *  (~7s total) beyond the default cadence — giving the user a
     *  clear beat to read/inspect the just-picked image. */
    const bump = () => setPauseUntil(Date.now() + AUTOPLAY_MS + INTERACTION_PAUSE_MS);

    const go = (dir) => {
        if (!canPaginate) return;
        const next = (imgIdx + dir + images.length) % images.length;
        setImgIdx(next);
    };

    // Autoplay — advance every AUTOPLAY_MS. Any change to ``imgIdx``
    // or ``pauseUntil`` re-arms the timer so manual interactions get
    // the extra grace period naturally.
    useEffect(() => {
        if (!canPaginate) return undefined;
        const wait = Math.max(AUTOPLAY_MS, pauseUntil - Date.now());
        const id = window.setTimeout(() => {
            setImgIdx((imgIdx + 1) % images.length);
        }, wait);
        return () => window.clearTimeout(id);
    }, [canPaginate, imgIdx, pauseUntil, images.length]);

    // Pause autoplay while the tab is backgrounded — bumps the
    // pauseUntil far into the future when hidden, resets to "now"
    // on refocus so the current image gets a fresh AUTOPLAY_MS.
    useEffect(() => {
        if (!canPaginate) return undefined;
        const onVis = () => {
            if (document.hidden) setPauseUntil(Date.now() + 60 * 60 * 1000);
            else setPauseUntil(Date.now());
        };
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, [canPaginate]);

    const onTouchStart = (e) => setDragStart(e.touches[0].clientX);
    const onTouchEnd = (e) => {
        if (dragStart == null) return;
        const dx = e.changedTouches[0].clientX - dragStart;
        setDragStart(null);
        if (Math.abs(dx) < 40) return;
        go(dx < 0 ? +1 : -1);
        bump();
    };
    return (
        <div
            className="relative bg-muted/30 select-none lg:sticky lg:top-6 lg:self-start lg:rounded-2xl lg:overflow-hidden"
            data-testid="pdp-image-carousel"
            onTouchStart={canPaginate ? onTouchStart : undefined}
            onTouchEnd={canPaginate ? onTouchEnd : undefined}
        >
            {images[imgIdx] ? (
                <img
                    src={images[imgIdx]}
                    alt={fallbackName}
                    className="w-full h-72 md:h-96 lg:h-[32rem] object-contain"
                    data-testid="pdp-image"
                    draggable={false}
                />
            ) : (
                <div className="w-full h-72 md:h-96 lg:h-[32rem] flex items-center justify-center">
                    <Package size={64} className="text-muted-foreground" />
                </div>
            )}
            <button
                onClick={onBack}
                data-testid="pdp-back"
                className="absolute top-4 left-4 bg-card/90 backdrop-blur-md rounded-full p-2 shadow-md"
            >
                <ArrowLeft size={20} />
            </button>
            {onViewCart && (
                <button
                    onClick={onViewCart}
                    aria-label="View cart"
                    data-testid="pdp-view-cart"
                    className="absolute top-4 right-4 bg-card/90 backdrop-blur-md rounded-full p-2 shadow-md"
                >
                    <ShoppingBag size={20} />
                    {cartCount > 0 && (
                        <span
                            data-testid="pdp-view-cart-count"
                            className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[10px] font-extrabold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center border-2 border-card"
                        >
                            {cartCount > 9 ? "9+" : cartCount}
                        </span>
                    )}
                </button>
            )}
            {canPaginate && (
                <>
                    {/* Desktop-only prev/next arrows — mobile relies on swipe. */}
                    <button
                        onClick={() => { go(-1); bump(); }}
                        data-testid="pdp-image-prev"
                        aria-label="Previous image"
                        className="hidden md:flex absolute top-1/2 left-3 -translate-y-1/2 bg-card/80 hover:bg-card backdrop-blur-md rounded-full p-2 shadow"
                    ><ArrowLeft size={18} /></button>
                    <button
                        onClick={() => { go(+1); bump(); }}
                        data-testid="pdp-image-next"
                        aria-label="Next image"
                        className="hidden md:flex absolute top-1/2 right-3 -translate-y-1/2 bg-card/80 hover:bg-card backdrop-blur-md rounded-full p-2 shadow rotate-180"
                    ><ArrowLeft size={18} /></button>
                    {/* Pushed below the top-4 View Cart button (added
                        alongside the back button) so the two never
                        overlap on multi-image products. */}
                    <div className="absolute top-[60px] right-3 bg-black/60 text-white text-[10px] font-bold rounded-full px-2 py-0.5" data-testid="pdp-image-counter">
                        {imgIdx + 1} / {images.length}
                    </div>
                    <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
                        {images.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => { setImgIdx(i); bump(); }}
                                data-testid={`pdp-image-dot-${i}`}
                                className={`h-2 rounded-full transition ${i === imgIdx ? "bg-white w-6" : "bg-white/50 w-2"}`}
                                aria-label={`Show image ${i + 1}`}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
